/**
 * Total Number Of Contracts — filter drawer (shared `ListFilterDrawer` pattern).
 */

import { useEffect, useState } from 'react'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { getCompanies, getProperties } from '@/features/invitee/services/inviteeService'
import { useUserRole } from '@/hooks/useUserRole'
import { ReportDateRangePicker } from './ReportDateRangePicker'

import type { TotalContractsFilterState } from '../types/filters'
import type { TotalContractsFiltersProps } from '../types/components'
import { TOTAL_CONTRACTS_PAYMENT_STATUS_OPTIONS as PAYMENT_STATUS_OPTIONS } from '../constants/filterOptions'
import {
  LABEL_CLASS,
} from '../constants/configuredReportStyles'
import {
  PAYMENT_SOURCE_FILTER_OPTIONS,
  SPECIAL_NOTE_FILTER_OPTIONS,
} from '../constants/filterOptions'


export function TotalContractsFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
  gatewayStatusOptions,
}: TotalContractsFiltersProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const { companyId, propertyId } = useUserRole()

  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const [draft, setDraft] = useState<TotalContractsFilterState>(filters)
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingProperties, setLoadingProperties] = useState(false)

  useEffect(() => {
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  useEffect(() => {
    if (!isAuthenticated) return
    const locked: Partial<TotalContractsFilterState> = {}
    if (lockedCompanyId && draft.company_id !== lockedCompanyId) {
      locked.company_id = lockedCompanyId
    }
    if (lockedPropertyId && draft.property_id !== lockedPropertyId) {
      locked.property_id = lockedPropertyId
    }
    if (Object.keys(locked).length > 0) {
      setDraft((prev) => ({ ...prev, ...locked }))
    }
  }, [isAuthenticated, lockedCompanyId, lockedPropertyId, draft.company_id, draft.property_id])

  const loadCompanies = async (search: string) => {
    if (!isAuthenticated || lockedCompanyId) return
    setLoadingCompanies(true)
    try {
      const data = await getCompanies(search)
      setCompanies(
        data.map((c) => ({
          value: String(c.company_id),
          label: c.company_name || String(c.company_id),
        })),
      )
    } finally {
      setLoadingCompanies(false)
    }
  }

  const loadProperties = async (search: string) => {
    if (!isAuthenticated || lockedPropertyId) return
    const companyIdForQuery = draft.company_id || lockedCompanyId
    setLoadingProperties(true)
    try {
      const data = await getProperties(search, companyIdForQuery)
      setProperties(
        data.map((p) => ({
          value: String(p.property_id),
          label: p.property_name || String(p.property_id),
        })),
      )
    } finally {
      setLoadingProperties(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated || !isOpen) return
    if (!lockedCompanyId) void loadCompanies('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isOpen, lockedCompanyId])

  useEffect(() => {
    if (!isAuthenticated || !isOpen || lockedPropertyId) return
    void loadProperties('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isOpen, draft.company_id, lockedCompanyId, lockedPropertyId])

  const patch = (partial: Partial<TotalContractsFilterState>) => {
    setDraft((prev) => ({ ...prev, ...partial }))
  }

  const companyOptions = lockedCompanyId
    ? [
        {
          value: lockedCompanyId,
          label: user?.current_role?.company_name || lockedCompanyId,
        },
      ]
    : companies

  const propertyOptions = lockedPropertyId
    ? [
        {
          value: lockedPropertyId,
          label: user?.current_role?.property_name || lockedPropertyId,
        },
      ]
    : properties

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix="total-contracts"
    >
      <div className="space-y-2" data-testid="total-contracts-filters-company-field">
        <label className={LABEL_CLASS}>Company</label>
        <SearchableSelect
          options={companyOptions}
          value={lockedCompanyId || draft.company_id || ''}
          onChange={(val) => {
            const company_id = Array.isArray(val) ? (val[0] ?? '') : val
            patch({ company_id, property_id: '' })
          }}
          onSearch={lockedCompanyId ? undefined : loadCompanies}
          onOpen={lockedCompanyId ? undefined : () => loadCompanies('')}
          loading={loadingCompanies}
          disabled={!!lockedCompanyId}
          placeholder="All Companies"
          searchPlaceholder="Search by company name, city, state"
          invokeSearchOnClose={false}
          triggerDataTestId="total-contracts-filters-company-trigger"
        />
      </div>

      <div className="space-y-2" data-testid="total-contracts-filters-property-field">
        <label className={LABEL_CLASS}>Property</label>
        <SearchableSelect
          options={propertyOptions}
          value={lockedPropertyId || draft.property_id || ''}
          onChange={(val) => {
            const property_id = Array.isArray(val) ? (val[0] ?? '') : val
            patch({ property_id })
          }}
          onSearch={lockedPropertyId ? undefined : loadProperties}
          onOpen={lockedPropertyId ? undefined : () => loadProperties('')}
          loading={loadingProperties}
          disabled={!!lockedPropertyId}
          placeholder="All Properties"
          searchPlaceholder="Search by property name, city, state"
          invokeSearchOnClose={false}
          triggerDataTestId="total-contracts-filters-property-trigger"
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS}>Special Note</label>
        <SearchableSelect
          searchable={false}
          options={SPECIAL_NOTE_FILTER_OPTIONS}
          value={draft.special_note}
          onChange={(val) => {
            const v = Array.isArray(val) ? val[0] : val
            patch({ special_note: v || '' })
          }}
          placeholder="Special Note"
          triggerDataTestId="total-contracts-filters-special-note"
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS}>Source</label>
        <SearchableSelect
          searchable={false}
          options={PAYMENT_SOURCE_FILTER_OPTIONS}
          value={draft.source}
          onChange={(val) => {
            const v = Array.isArray(val) ? val[0] : val
            patch({ source: v || '' })
          }}
          placeholder="All Source"
          triggerDataTestId="total-contracts-filters-source"
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS}>Statuses</label>
        <SearchableSelect
          multiple
          options={PAYMENT_STATUS_OPTIONS}
          value={draft.status}
          onChange={(val) => patch({ status: Array.isArray(val) ? val : [] })}
          placeholder="All Statuses"
          triggerDataTestId="total-contracts-filters-status-trigger"
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS}>Gateway Status</label>
        <SearchableSelect
          multiple
          options={gatewayStatusOptions}
          value={draft.gateway_status}
          onChange={(val) => patch({ gateway_status: Array.isArray(val) ? val : [] })}
          placeholder="Gateway Status"
          triggerDataTestId="total-contracts-filters-gateway-trigger"
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS}>Transaction Dates</label>
        <ReportDateRangePicker
          startDate={draft.fromDt}
          endDate={draft.toDt}
          onChange={(start, end) =>
            patch({
              fromDt: start ? formatDateForAPI(start) : '',
              toDt: end ? formatDateForAPI(end) : '',
            })
          }
          placeholder="Select Date Range"
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS}>Contract Start Dates</label>
        <ReportDateRangePicker
          startDate={draft.contract_start_fromDt}
          endDate={draft.contract_start_toDt}
          onChange={(start, end) =>
            patch({
              contract_start_fromDt: start ? formatDateForAPI(start) : '',
              contract_start_toDt: end ? formatDateForAPI(end) : '',
            })
          }
          placeholder="Select Date Range"
        />
      </div>
    </ListFilterDrawer>
  )
}

export type { TotalContractsFilterState } from '../types/filters'

